import { useMemo, useRef, useState } from 'react';
import { Download, Upload, Rocket, Archive, Smartphone, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  usePosAppVersions,
  useUploadPosAppVersion,
  usePublishPosAppVersion,
  useArchivePosAppVersion,
  useDeletePosAppVersion,
  posAppVersionDownloadUrl,
  type PosAppVersion,
  type ReleaseApp,
} from '@/hooks/use-pos-app-versions';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';

/** Message out of an API error, falling back to something a person can act on. */
function errorMessage(err: unknown, fallback: string): string {
  return (err as { error?: { message?: string } })?.error?.message || fallback;
}

function formatSize(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function StatusBadge({ status }: { status: PosAppVersion['status'] }) {
  if (status === 'published') return <Badge variant="success">Published</Badge>;
  if (status === 'testing') return <Badge variant="warning">Testing</Badge>;
  return <Badge variant="default">Archived</Badge>;
}

/** The words that differ between the apps. Everything else is shared. */
const APP_COPY: Record<
  ReleaseApp,
  {
    tab: string;
    /** Plural, as in "publish to registers". */
    devices: string;
    /** Singular, as in "every eligible register". */
    device: string;
    versionNamePlaceholder: string;
    versionCodePlaceholder: string;
    notesPlaceholder: string;
    pollNote: string;
  }
> = {
  pos: {
    tab: 'POS App',
    devices: 'registers',
    device: 'register',
    versionNamePlaceholder: '1.6.37',
    versionCodePlaceholder: '266',
    notesPlaceholder: 'What changed in this build — shown to the cashier in the update prompt.',
    // Registers poll; nothing is pushed to them. Said here so an admin
    // watching a till does not read a half-hour delay as a failure.
    pollNote:
      'Registers look for it at sign-in and about every 30 minutes while in use, so a signed-in register can take up to half an hour to show the prompt.',
  },
  mobile: {
    tab: 'Mobile Apps',
    devices: 'phones',
    device: 'phone',
    versionNamePlaceholder: '1.0.15',
    versionCodePlaceholder: '17',
    notesPlaceholder: 'What changed in this build — shown to staff in the update prompt on their phone.',
    pollNote:
      'Phones look for it when the app is opened or brought back to the front, and about every 30 minutes while in use.',
  },
  cds: {
    tab: 'Customer Display',
    devices: 'customer displays',
    device: 'customer display',
    versionNamePlaceholder: '1.2.13',
    versionCodePlaceholder: '15',
    notesPlaceholder: 'What changed in this build — shown on the customer display’s update prompt.',
    // A display has no login of its own; the register it is attached to asks
    // on its behalf (with its device token), so the timing is the register's.
    pollNote:
      'Displays hear about it through the register they are connected to, which checks about every 30 minutes while a display is attached and again whenever the display app reconnects. The prompt waits for the display’s welcome screen.',
  },
};

const TAB_PATH: Record<ReleaseApp, string> = {
  pos: '/admin/pos-app-versions',
  mobile: '/admin/pos-app-versions/mobile',
  cds: '/admin/pos-app-versions/cds',
};

/**
 * Super Admin → App Versions, with a tab per application.
 *
 * Each tab is its own hash route, so a link to the mobile or Customer Display
 * releases lands on them and the original POS URL still opens the POS tab. The tabs share every
 * piece of UI but none of the data: each talks to its own API namespace.
 */
export function AdminPosAppVersionsPage({ app = 'pos' }: { app?: ReleaseApp }) {
  return (
    <div className="space-y-6">
      <PageHeader title="App Versions" description="Upload a build, test it, then publish it to devices" />

      <div className="border-b border-border">
        <div className="flex w-full min-w-0 overflow-x-auto" role="tablist" aria-label="Applications">
          {(Object.keys(APP_COPY) as ReleaseApp[]).map((tab) => (
            <a
              key={tab}
              href={`#${TAB_PATH[tab]}`}
              role="tab"
              aria-selected={app === tab}
              className={cn(
                'relative shrink-0 px-4 py-2.5 text-sm font-medium transition-colors',
                app === tab ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {APP_COPY[tab].tab}
              {app === tab && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </a>
          ))}
        </div>
      </div>

      {/* Keyed so switching tabs starts from a clean form and clean dialogs. */}
      <AppVersionsPanel key={app} app={app} />
    </div>
  );
}

/**
 * One application's releases.
 *
 * Uploading a build is not releasing it: an upload lands in Testing, where the
 * team downloads and installs it by hand, and a separate Publish is what offers
 * it to devices. The panel is built around keeping those two acts visibly
 * different — the live version is called out at the top, and Publish asks for
 * confirmation naming the version it replaces.
 */
function AppVersionsPanel({ app }: { app: ReleaseApp }) {
  const copy = APP_COPY[app];
  const { data, isLoading } = usePosAppVersions(app);
  const uploadVersion = useUploadPosAppVersion(app);
  const publishVersion = usePublishPosAppVersion(app);
  const archiveVersion = useArchivePosAppVersion(app);
  const deleteVersion = useDeletePosAppVersion(app);

  const [file, setFile] = useState<File | null>(null);
  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Bytes sent so far, while an upload is in flight. Null otherwise. */
  const [uploadProgress, setUploadProgress] = useState<{ loaded: number; total: number } | null>(null);

  const [publishTarget, setPublishTarget] = useState<PosAppVersion | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<PosAppVersion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PosAppVersion | null>(null);

  // Newest build first: by version code, then upload time, so a build uploaded
  // out of order still lands where its version says it belongs.
  const versions = useMemo(
    () =>
      [...(data?.versions ?? [])].sort(
        (a, b) => b.versionCode - a.versionCode || b.uploadedAt.localeCompare(a.uploadedAt),
      ),
    [data?.versions],
  );
  const published = data?.published ?? null;

  const resetForm = () => {
    setFile(null);
    setVersionName('');
    setVersionCode('');
    setReleaseNotes('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.apk')) {
      toast.error('Please select an .apk file');
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploadProgress({ loaded: 0, total: file.size });
    try {
      const { version } = await uploadVersion.mutateAsync({
        file,
        versionName,
        versionCode,
        releaseNotes,
        onProgress: (loaded, total) => setUploadProgress({ loaded, total }),
      });
      toast.success(`Uploaded ${version.versionName} (${version.versionCode}) — available for testing`);
      resetForm();
    } catch (err) {
      // The server's message names what is actually wrong with the file — the
      // wrong package, a mismatched signing certificate, a duplicate version
      // code — so it is worth showing verbatim.
      toast.error(errorMessage(err, 'Upload failed'));
    } finally {
      setUploadProgress(null);
    }
  };

  // Once every byte has left the browser the server still has to hash the
  // file, read the manifest and copy it to S3 — a minute or more on a big
  // build. Said out loud so a full bar that has not finished is not a hang.
  const uploadPercent = uploadProgress
    ? Math.min(100, Math.round((uploadProgress.loaded / uploadProgress.total) * 100))
    : 0;
  const uploadSent = uploadProgress !== null && uploadProgress.loaded >= uploadProgress.total;

  const handlePublish = async () => {
    if (!publishTarget) return;
    try {
      await publishVersion.mutateAsync(publishTarget.id);
      toast.success(`${publishTarget.versionName} is now the published version`);
      setPublishTarget(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Publish failed'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVersion.mutateAsync(deleteTarget.id);
      toast.success(`${deleteTarget.versionName} (${deleteTarget.versionCode}) deleted`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Delete failed'));
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await archiveVersion.mutateAsync(archiveTarget.id);
      toast.success(`${archiveTarget.versionName} archived`);
      setArchiveTarget(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Archive failed'));
    }
  };

  return (
    <div className="space-y-6">
      {/* What devices are being offered right now — the one fact worth reading
          before anything else on this page. */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-3">
          <Smartphone className="h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">Currently published to {copy.devices}</p>
            {published ? (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-lg font-semibold">
                  {published.versionName} <span className="text-muted-foreground">({published.versionCode})</span>
                </p>
                <Badge variant="teal">Latest</Badge>
              </div>
            ) : (
              <p className="text-lg font-semibold text-muted-foreground">
                No published version — {copy.devices} stay on what they have
              </p>
            )}
          </div>
          {/* Same real-link download as the history rows below. */}
          {published && (
            <a
              href={posAppVersionDownloadUrl(published.id, app)}
              download
              className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          )}
        </div>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <h2 className="font-semibold">Upload a build</h2>
        <p className="text-sm text-muted-foreground">
          The package name, version and signing certificate are read from the APK itself and checked before it is
          accepted. Uploading does not notify any {copy.device}.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="apk-file">APK file</Label>
            <input
              id="apk-file"
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={handleFileSelect}
              disabled={uploadVersion.isPending}
              className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm"
            />
            {file && (
              <p className="mt-1 text-xs text-muted-foreground">
                {file.name} · {formatSize(file.size)}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="version-name">Version name</Label>
            <Input
              id="version-name"
              placeholder={copy.versionNamePlaceholder}
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="version-code">Version code</Label>
            <Input
              id="version-code"
              inputMode="numeric"
              placeholder={copy.versionCodePlaceholder}
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="release-notes">Release notes</Label>
            <textarea
              id="release-notes"
              rows={3}
              className="w-full rounded-md border border-border bg-background p-2 text-sm"
              placeholder={copy.notesPlaceholder}
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
            />
          </div>
        </div>

        <Button onClick={handleUpload} disabled={!file || uploadVersion.isPending}>
          <Upload className="mr-2 h-4 w-4" />
          {uploadVersion.isPending
            ? uploadSent
              ? 'Checking the file…'
              : `Uploading… ${uploadPercent}%`
            : 'Upload APK'}
        </Button>

        {uploadProgress && (
          <div className="space-y-1" role="status" aria-live="polite">
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={uploadPercent}
            >
              <div
                className={`h-full rounded-full bg-primary transition-[width] duration-300 ${uploadSent ? 'animate-pulse' : ''}`}
                style={{ width: `${uploadPercent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {uploadSent
                ? `${formatSize(uploadProgress.total)} sent. Checking the APK and storing it — this can take a minute on a large build.`
                : `${formatSize(uploadProgress.loaded)} of ${formatSize(uploadProgress.total)} · ${uploadPercent}%`}
            </p>
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4">
          <h2 className="font-semibold">Version history</h2>
        </div>

        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : versions.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No builds uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Version</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">File</th>
                  <th className="p-3 font-medium">Uploaded</th>
                  <th className="p-3 font-medium">Published</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.id} className="border-b border-border last:border-0">
                    <td className="p-3">
                      <div className="font-medium">{version.versionName}</div>
                      <div className="text-xs text-muted-foreground">code {version.versionCode}</div>
                      {version.releaseNotes && (
                        <div className="mt-1 max-w-md whitespace-pre-wrap text-xs text-muted-foreground">
                          {version.releaseNotes}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={version.status} />
                    </td>
                    <td className="p-3">
                      <div>{version.apkFileName}</div>
                      <div className="text-xs text-muted-foreground">{formatSize(version.fileSizeBytes)}</div>
                    </td>
                    <td className="p-3">
                      <div>{formatDate(version.uploadedAt)}</div>
                      <div className="text-xs text-muted-foreground">{version.uploadedByName ?? '—'}</div>
                    </td>
                    <td className="p-3">
                      <div>{formatDate(version.publishedAt)}</div>
                      <div className="text-xs text-muted-foreground">{version.publishedByName ?? '—'}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        {/* A link, not a fetch: the session cookie rides along
                            and the browser streams the APK straight to disk. */}
                        {/* A real link, not a button and not a fetch: the session
                            cookie rides along on a same-origin request and the
                            browser streams the APK to disk instead of holding
                            200 MB in memory as a blob. Styled as a small
                            secondary button by hand — Button renders a plain
                            <button> with no asChild slot. */}
                        <a
                          href={posAppVersionDownloadUrl(version.id, app)}
                          download
                          title="Download"
                          aria-label={`Download ${version.versionName}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-muted"
                        >
                          <Download className="h-4 w-4" />
                        </a>

                        {version.status === 'testing' && (
                          <Button
                            size="icon"
                            className="h-8 w-8"
                            title="Publish"
                            aria-label={`Publish ${version.versionName}`}
                            onClick={() => setPublishTarget(version)}
                          >
                            <Rocket className="h-4 w-4" />
                          </Button>
                        )}

                        {version.status !== 'published' && version.status !== 'archived' && (
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-8 w-8"
                            title="Archive"
                            aria-label={`Archive ${version.versionName}`}
                            onClick={() => setArchiveTarget(version)}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Never offered for the published build: devices are
                            being pointed at it and one may be mid-download. */}
                        {version.status !== 'published' && (
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-8 w-8"
                            title="Delete"
                            aria-label={`Delete ${version.versionName}`}
                            onClick={() => setDeleteTarget(version)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={publishTarget !== null}
        variant="warning"
        title={`Publish this version to ${copy.devices}?`}
        message={
          publishTarget
            ? `${publishTarget.versionName} (code ${publishTarget.versionCode}) becomes the version every eligible ${copy.device} is offered.` +
              (published
                ? ` It replaces ${published.versionName} (code ${published.versionCode}), which is archived.`
                : ' No version is published today, so this is the first.') +
              ` ${copy.pollNote}`
            : ''
        }
        confirmLabel="Publish"
        loading={publishVersion.isPending}
        onConfirm={handlePublish}
        onCancel={() => setPublishTarget(null)}
      />

      <ConfirmDialog
        open={archiveTarget !== null}
        title="Archive this version?"
        message={
          archiveTarget
            ? `${archiveTarget.versionName} (code ${archiveTarget.versionCode}) is kept for history but can no longer be published.`
            : ''
        }
        confirmLabel="Archive"
        loading={archiveVersion.isPending}
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        variant="danger"
        title="Delete this build?"
        message={
          deleteTarget
            ? `${deleteTarget.versionName} (code ${deleteTarget.versionCode}) is removed from this list and its APK is deleted from storage. This cannot be undone; the same version code can be uploaded again afterwards.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleteVersion.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
